// libraries
import type { AnyFSA } from '@makemydeal/dr-browser-utils';
import type { StateTree } from '@makemydeal/dr-dash-types';
import type { ReduxStore } from '@makemydeal/dr-shared-store';

export const CALC_REQUEST_ID_META = 'calcRequestId';

type RequestIdState = {
    latest: number;
    /** The id served to the most recent grid round, so every row fanned out from one grid edit shares it. */
    round?: { id: string; requestId: number };
};

const requestIdByStoreScope = new WeakMap<object, Map<string, RequestIdState>>();

const getRequestIdState = (store: ReduxStore<StateTree>): RequestIdState => {
    const realStore: object = (store as unknown as { __realStore?: object }).__realStore ?? store;
    const scopeId = (store.getState() as { __scopeId?: string }).__scopeId ?? '__primary';
    let byScope = requestIdByStoreScope.get(realStore);
    if (!byScope) {
        byScope = new Map();
        requestIdByStoreScope.set(realStore, byScope);
    }
    let counter = byScope.get(scopeId);
    if (!counter) {
        counter = { latest: 0 };
        byScope.set(scopeId, counter);
    }
    return counter;
};

export const getLatestRequestId = (store: ReduxStore<StateTree>): number => getRequestIdState(store).latest;

export const stampRequestId = (store: ReduxStore<StateTree>, action: AnyFSA): number => {
    const existing = action.meta?.[CALC_REQUEST_ID_META];
    if (typeof existing === 'number') {
        return existing;
    }
    const requestIds = getRequestIdState(store);

    // The rows fanned out from one grid edit are siblings, not rivals - each writes its own cell. Stamped individually,
    // every row but the last was marked stale and its response thrown away.
    const roundId = action.meta?.paymentGrid?.roundId;
    if (typeof roundId === 'string') {
        if (requestIds.round?.id !== roundId) {
            requestIds.latest += 1;
            requestIds.round = { id: roundId, requestId: requestIds.latest };
        }
        action.meta = { ...action.meta, [CALC_REQUEST_ID_META]: requestIds.round.requestId };
        return requestIds.round.requestId;
    }

    requestIds.latest += 1;
    action.meta = { ...action.meta, [CALC_REQUEST_ID_META]: requestIds.latest };
    return requestIds.latest;
};

export const markStalePaymentResponse = (store: ReduxStore<StateTree>, action: AnyFSA): boolean => {
    const originId = (action.meta?.originalAction as AnyFSA | undefined)?.meta?.[CALC_REQUEST_ID_META];
    if (typeof originId !== 'number' || originId >= getLatestRequestId(store)) {
        return false;
    }
    action.meta = { ...action.meta, isStalePaymentResponse: true };
    return true;
};
